// Arbol anidado a cualquier profundidad a partir de la cadena de grupos de cada
// elemento. `chainOf` devuelve la cadena root-first ([{ id, name }, ...]) y cada
// nodo agrupa en `locations` los elementos cuya cadena termina en ese grupo.
//
// El orden de hermanos por defecto es el de PRIMERA APARICION en `items`, que
// no es el orden real del admin (alfabetico/custom por nivel) cuando `items`
// viene de una fuente externa ya aplanada (p. ej. el resultado de un reporte).
// `orderIndex` (locationsStore.groupOrderIndex) resuelve eso: reordena cada
// nivel de hermanos por su rango real, dejando al final los que no aparezcan
// en el mapa.
export function buildChainTree (items, chainOf, orderIndex = null) {
  const roots = []
  const nodesById = new Map()

  items.forEach(item => {
    const chain = chainOf(item) || []
    if (chain.length === 0) return

    let siblings = roots
    let node = null

    chain.forEach((group, index) => {
      let current = nodesById.get(group.id)

      if (!current) {
        current = { id: group.id, name: group.name, level: index + 1, locations: [], children: [] }
        nodesById.set(group.id, current)
        siblings.push(current)
      }

      node = current
      siblings = current.children
    })

    node.locations.push(item)
  })

  if (orderIndex) sortChainTreeByOrderIndex(roots, orderIndex)

  return roots
}

function sortChainTreeByOrderIndex (nodes, orderIndex) {
  nodes.sort((a, b) => {
    const rankA = orderIndex[a.id] ?? Infinity
    const rankB = orderIndex[b.id] ?? Infinity
    return rankA - rankB
  })

  nodes.forEach(node => sortChainTreeByOrderIndex(node.children, orderIndex))
}

// Filas planas root-first para pintar el arbol sin anidar plantillas: una fila
// `group` por grupo y, si tiene ubicaciones propias, una fila `locations` con
// todas ellas. `depth` da la indentacion.
export function flattenChainTree (nodes, depth = 0) {
  return nodes.flatMap(node => {
    const rows = [{ type: 'group', key: `group-${node.id}`, id: node.id, name: node.name, depth }]

    if (node.locations.length > 0) {
      rows.push({
        type: 'locations',
        key: `locations-${node.id}`,
        id: node.id,
        locations: node.locations,
        depth: depth + 1
      })
    }

    return [...rows, ...flattenChainTree(node.children, depth + 1)]
  })
}

// Cadena derivada de los campos top/middle/bottom que el backend sigue
// enviando: raiz, padre inmediato del grupo propio y grupo propio. Respaldo de
// a lo mas 3 niveles — puede omitir niveles intermedios en arboles mas
// profundos — para cuando el arbol de grupos del store no esta cargado.
export function legacyGroupChain (row) {
  const chain = []

  ;[
    { id: row.top_id, name: row.top_name },
    { id: row.middle_id, name: row.middle_name },
    { id: row.bottom_id, name: row.bottom_name }
  ].forEach(group => {
    if (group.id && !chain.some(added => added.id === group.id)) chain.push(group)
  })

  return chain
}

// Ruta root-first de una cadena de grupos, p. ej. "Region / Zona / Distrito".
export function chainPath (chain, separator = ' / ') {
  return (chain || [])
    .map(group => typeof group === 'string' ? group : group.name)
    .filter(Boolean)
    .join(separator)
}

// Columnas de grupo para exports tabulares (Excel): una por nivel, "Grupo 1..N",
// con un minimo de 3 para conservar la estructura historica que los clientes
// ya consumen en sus hojas. Cada fila lleva un nombre por columna ('' si no hay).
const MIN_GROUP_COLUMNS = 3

export function groupColumnsLayout (chains, minColumns = MIN_GROUP_COLUMNS) {
  const count = Math.max(minColumns, ...(chains || []).map(chain => (chain || []).length))
  return {
    count,
    headers: Array.from({ length: count }, (_, i) => `Grupo ${i + 1}`)
  }
}

export function chainToColumns (chain, count) {
  const names = (chain || []).map(group => typeof group === 'string' ? group : group.name)
  return Array.from({ length: count }, (_, i) => names[i] || null)
}

// Grupo de `candidates` (ids) del que desciende la cadena, o null. Equivale al
// resolveAncestorAmong del backend, resuelto en el front.
export function resolveAncestorAmong (chain, candidateIds) {
  const candidates = new Set(candidateIds)
  const match = (chain || []).find(group => candidates.has(group.id))

  return match || null
}
